/**
 * Messages Hooks
 *
 * React Query hooks for message template management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSdkContext } from '../providers/SdkProvider';
import {
  createMessagesService,
  type CreateMessageTemplateInput,
  type UpdateMessageTemplateInput,
  type MessageTemplate,
  type SendMessageTemplateInput,
  type TestSendResult,
  type PreviewMessageTemplateInput,
  type PreviewMessageTemplateResult,
} from '../services/messages.service';

// ═══════════════════════════════════════════════════════════════════════
// QUERY KEYS
// ═══════════════════════════════════════════════════════════════════════

export const messagesKeys = {
  all: ['messages'] as const,
  templates: () => [...messagesKeys.all, 'templates'] as const,
  template: (id: string) => [...messagesKeys.all, 'template', id] as const,
  analytics: () => [...messagesKeys.all, 'analytics'] as const,
};

// ═══════════════════════════════════════════════════════════════════════
// QUERY HOOKS
// ═══════════════════════════════════════════════════════════════════════

export function useMessageTemplates() {
  const { client } = useSdkContext();
  const service = createMessagesService(client);

  return useQuery({
    queryKey: messagesKeys.templates(),
    queryFn: () => service.getTemplates(),
  });
}

export function useMessageTemplate(id: string) {
  const { client } = useSdkContext();
  const service = createMessagesService(client);

  return useQuery({
    queryKey: messagesKeys.template(id),
    queryFn: () => service.getTemplate(id),
    enabled: !!id,
  });
}

export function useMessageAnalytics() {
  const { client } = useSdkContext();
  const service = createMessagesService(client);

  return useQuery({
    queryKey: messagesKeys.analytics(),
    queryFn: () => service.getAnalytics(),
  });
}

// ═══════════════════════════════════════════════════════════════════════
// MUTATION HOOKS
// ═══════════════════════════════════════════════════════════════════════

export function useCreateMessageTemplate() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const service = createMessagesService(client);

  return useMutation({
    mutationFn: (input: CreateMessageTemplateInput) => service.createTemplate(input),
    onSuccess: (newTemplate) => {
      queryClient.setQueryData<MessageTemplate[]>(messagesKeys.templates(), (old) =>
        old ? [newTemplate, ...old] : [newTemplate]
      );
    },
  });
}

export function useUpdateMessageTemplate() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const service = createMessagesService(client);

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateMessageTemplateInput }) =>
      service.updateTemplate(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagesKeys.templates() });
    },
  });
}

export function useDeleteMessageTemplate() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const service = createMessagesService(client);

  return useMutation({
    mutationFn: (id: string) => service.deleteTemplate(id),
    onSuccess: (_, deletedId) => {
      queryClient.setQueryData<MessageTemplate[]>(messagesKeys.templates(), (old) =>
        old ? old.filter((t) => t.id !== deletedId) : []
      );
    },
  });
}

/**
 * Duplicate a template. On success, the new row is prepended to the
 * cached list so the admin sees it appear without a refetch.
 */
export function useDuplicateMessageTemplate() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const service = createMessagesService(client);

  return useMutation({
    mutationFn: (id: string) => service.duplicateTemplate(id),
    onSuccess: (copy) => {
      queryClient.setQueryData<MessageTemplate[]>(messagesKeys.templates(), (old) =>
        old ? [copy, ...old] : [copy]
      );
    },
  });
}

/**
 * Restore a system_default template to its seeded contents. Invalidates
 * the templates list because multiple fields change at once (subject,
 * body, per-channel bodies, `customized` flag).
 */
export function useRestoreMessageTemplate() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const service = createMessagesService(client);

  return useMutation({
    mutationFn: (id: string) => service.restoreTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagesKeys.templates() });
    },
  });
}

/**
 * Send a test email of this template to the logged-in admin. Returns
 * `{ sent, to }` so the caller can surface "Sent to alice@acme.com"
 * confirmation in the toast.
 */
export function useTestSendMessageTemplate() {
  const { client } = useSdkContext();
  const service = createMessagesService(client);

  return useMutation<TestSendResult, Error, string>({
    mutationFn: (id: string) => service.testSendTemplate(id),
  });
}

/**
 * Render a draft template body through the email shell. The editor
 * calls this with debounced input changes and drops the returned
 * `html` into an iframe's srcdoc for a live preview. Not cached —
 * the editor owns state.
 */
export function usePreviewMessageTemplate() {
  const { client } = useSdkContext();
  const service = createMessagesService(client);

  return useMutation<PreviewMessageTemplateResult, Error, PreviewMessageTemplateInput>({
    mutationFn: (input) => service.previewTemplate(input),
  });
}

export function useSendMessage() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const service = createMessagesService(client);

  return useMutation({
    mutationFn: (data: SendMessageTemplateInput) => service.sendMessage(data),
    onSuccess: () => {
      // Analytics recompute from `message_sends` → invalidate so the cards
      // on the Messages page refresh. Templates also refresh so per-card
      // sent counts (derived from the backend) stay in sync.
      queryClient.invalidateQueries({ queryKey: messagesKeys.analytics() });
      queryClient.invalidateQueries({ queryKey: messagesKeys.templates() });
    },
  });
}
